// Snake & Ladder board renderer.
//
// One responsive SVG that draws the 10×10 grid, every snake curve,
// every ladder, and every player token. Tokens animate to their new
// positions via CSS transitions on the SVG transform.
//
// Ludo-parity interaction model: after the human rolls, the tokens that
// can legally move are highlighted (pulsing gold ring) and become
// CLICKABLE — tapping the token on the board issues the move, exactly
// like tapping a pawn in Ludo. No separate A/B buttons.

import { useMemo } from 'react';
import {
  BOARD_VIEW, CELL_SIZE,
  centerOf, topLeftOf,
  ladderLine, snakeGeometry, snakeKind,
} from './board';

export type SnakeData  = { head: number; tail: number };
export type LadderData = { base: number; top: number };

export type TokenRender = {
  ownerColor: string;    // colour name, e.g. 'red' | 'green' | 'yellow' | 'blue'
  letter: 'A' | 'B';
  position: number;      // 0 = off-board, 1..100 = on a square
  isHomed: boolean;
  slotIndex?: number;    // this token's order among all tokens on its square
  slotCount?: number;    // total tokens sharing the square (for cluster layout)
  clickable?: boolean;   // true → eligible to move this turn (pulses + tappable)
};

// Egg display scale for a LONE egg out on the board (squares 2..99). It's
// deliberately bigger than the old circle token (~36px) so the egg — and
// its legs while running — read clearly. Start (sq 1) & nest (sq 100) stay
// small (see eggSlot) because up to 8 eggs cluster there.
export const EGG_RUN_SCALE = 1.6;

// Cluster layout for N eggs sharing one square. `place` controls size:
//   'start' (square 1) / 'home' (square 100) → SMALL so 8 eggs fit;
//   'normal' (squares 2..99)                 → BIG & clearly visible.
// At the nest, eggs huddle lower so they read as "resting in the nest".
function eggSlot(i: number, n: number, place: 'start' | 'home' | 'normal'): { dx: number; dy: number; scale: number } {
  const isHome = place === 'home';
  const big = place === 'normal';
  if (n <= 1) return { dx: 0, dy: isHome ? 14 : 0, scale: isHome ? 0.72 : (big ? EGG_RUN_SCALE : 0.85) };
  const cols = n <= 2 ? n : n <= 4 ? 2 : n <= 6 ? 3 : 4;
  const rows = Math.ceil(n / cols);
  const col = i % cols;
  const row = Math.floor(i / cols);
  const maxS = big ? 1.15 : (isHome ? 0.7 : 0.8);
  const minS = big ? 0.66 : 0.5;
  const scale = Math.max(minS, Math.min(maxS, (big ? 4.4 : 3.6) / (cols + rows)));
  const gap = (big ? 30 : 28) * scale + 4;
  const dx = (col - (cols - 1) / 2) * gap;
  let dy = (row - (rows - 1) / 2) * gap;
  if (isHome) dy += 14;   // nudge the whole nest-cluster toward the nest (lower cell)
  return { dx, dy, scale };
}

type Props = {
  snakes:  SnakeData[];
  ladders: LadderData[];
  tokens:  TokenRender[];
  /** Per-step transition duration (ms) — short for square hops, longer
   *  for snake/ladder slides. Driven by the page's animation player. */
  moveMs?: number;
  /** Ref to the <svg> so the page can grab token nodes (data-tok) and
   *  drive imperative WAAPI move animations on them. */
  svgRef?: React.RefObject<SVGSVGElement>;
  /** `${color}-${letter}` of the token currently being WAAPI-animated —
   *  its CSS transition is disabled so the imperative animation + the
   *  final React commit don't double-slide. */
  animatingKey?: string | null;
  /** Fired when the human taps an eligible (clickable) token. */
  onPickToken?: (letter: 'A' | 'B') => void;
};

const COLOR_BY_NAME: Record<string, string> = {
  red:    '#ef4444',
  green:  '#22c55e',
  yellow: '#facc15',
  blue:   '#3b82f6',
};

export function getColorHex(name: string): string {
  return COLOR_BY_NAME[name] || '#888';
}

// Pastel checkerboard palette. Every cell is a soft, near-flat pastel.
// Each hue has a LIGHT and a (slightly more prominent) DARK tint; the
// checker parity decides which one a cell uses, so neighbours alternate
// light↔dark like a chessboard. The hue itself cycles in 2×2 blocks so
// the board shows several colours flowing diagonally — but every cell
// still alternates light/dark with all four of its neighbours.
const CELL_HUES: Array<{ light: string; dark: string }> = [
  { light: '#f6dde8', dark: '#eec3d5' }, // pink
  { light: '#fbe7d5', dark: '#f4cead' }, // peach
  { light: '#f8f1cf', dark: '#ebe0a2' }, // butter
  { light: '#dcefdc', dark: '#bbe2bb' }, // mint
  { light: '#dbe8f6', dark: '#b7d0ee' }, // sky
  { light: '#e8def3', dark: '#d1bee9' }, // lilac
];

export default function SnakeLadderBoard({
  snakes, ladders, tokens, moveMs = 200, svgRef, animatingKey, onPickToken,
}: Props) {
  // Highlight every square that holds a clickable token so the player's
  // eye is drawn to where they can move.
  const eligibleSquares = useMemo(
    () => new Set(tokens.filter((t) => t.clickable).map((t) => t.position)),
    [tokens],
  );
  // Render clickable tokens LAST so they sit on TOP of the stack and are
  // always tappable even when an opponent token shares the square.
  const orderedTokens = useMemo(
    () => [...tokens].sort((a, b) => Number(!!a.clickable) - Number(!!b.clickable)),
    [tokens],
  );

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${BOARD_VIEW} ${BOARD_VIEW}`}
      preserveAspectRatio="xMidYMid meet"
      className="sl-board-svg block w-full h-auto select-none"
      role="img"
      aria-label="Snake and Ladder board"
    >
      <defs>
        <radialGradient id="sl-snake-head" cx="40%" cy="35%" r="70%">
          <stop offset="0%"  stopColor="#6ee7a0" />
          <stop offset="100%" stopColor="#15803d" />
        </radialGradient>
        <radialGradient id="sl-goal" cx="50%" cy="40%" r="75%">
          <stop offset="0%"  stopColor="#fff7cc" />
          <stop offset="55%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#b45309" />
        </radialGradient>
      </defs>

      {/* L1 — square grid (green felt, gold numbers). Square 100 is the
          GOAL: gold cell, glowing ring and a trophy so it clearly reads
          as where both tokens must finish. */}
      {Array.from({ length: 100 }, (_, i) => i + 1).map((n) => {
        const { x, y } = topLeftOf(n);
        const c = centerOf(n);
        const row = Math.floor((n - 1) / 10);          // rowFromBottom 0..9
        const col = (n - 1) % 10;                        // numbering column
        // Boustrophedon: odd rows run right-to-left, so the VISUAL column
        // is mirrored there. Using the visual column gives a true
        // chessboard (vertical AND horizontal alternation); the old code
        // used the numbering column, which produced vertical stripes.
        const vcol = row % 2 === 0 ? col : 9 - col;
        const parity = (row + vcol) % 2;                 // 0 = light, 1 = dark
        const hue = CELL_HUES[(Math.floor(row / 2) + Math.floor(vcol / 2)) % CELL_HUES.length];
        const isGoal = n === 100;
        const fill = isGoal ? '#fbeecb' : (parity === 0 ? hue.light : hue.dark);
        const highlighted = eligibleSquares.has(n);
        return (
          <g key={n}>
            <rect
              x={x} y={y}
              width={CELL_SIZE} height={CELL_SIZE}
              fill={fill}
              stroke={isGoal ? '#fde68a' : 'rgba(120,84,48,0.22)'}
              strokeWidth={isGoal ? 3 : 1}
            />
            {isGoal && (
              <>
                <rect x={x + 2} y={y + 2} width={CELL_SIZE - 4} height={CELL_SIZE - 4} rx={8}
                      fill="none" stroke="#e0b84e" strokeWidth={2.5}>
                  <animate attributeName="opacity" values="0.4;0.95;0.4" dur="1.8s" repeatCount="indefinite" />
                </rect>
                <NestAndHen cx={c.x} top={y} />
              </>
            )}
            {highlighted && (
              <rect
                x={x + 2} y={y + 2}
                width={CELL_SIZE - 4} height={CELL_SIZE - 4}
                fill="none"
                stroke="#11331f"
                strokeWidth={3.5}
                rx={6}
              >
                <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite" />
              </rect>
            )}
            {!isGoal && (
              <text
                x={x + 7} y={y + 18}
                fontSize={13}
                fontWeight="bold"
                fill="rgba(74,52,33,0.66)"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
              >
                {n}
              </text>
            )}
          </g>
        );
      })}

      {/* L2 — ladders. Two parallel rails + rungs across. */}
      {ladders.map((l, idx) => {
        const line = ladderLine(l.base, l.top);
        const width = 26;
        const offX = Math.cos(line.angleRad + Math.PI / 2) * (width / 2);
        const offY = Math.sin(line.angleRad + Math.PI / 2) * (width / 2);
        const numRungs = Math.max(2, Math.floor(line.length / 35));
        const rungs = [];
        for (let i = 1; i < numRungs; i++) {
          const cx = line.x1 + (line.x2 - line.x1) * (i / numRungs);
          const cy = line.y1 + (line.y2 - line.y1) * (i / numRungs);
          rungs.push(
            <line
              key={i}
              x1={cx + offX} y1={cy + offY}
              x2={cx - offX} y2={cy - offY}
              stroke="#9a6a3a"
              strokeWidth={4}
              strokeLinecap="round"
            />,
          );
        }
        return (
          <g key={`ladder-${idx}`} opacity={0.97}>
            <line x1={line.x1 + offX} y1={line.y1 + offY} x2={line.x2 + offX} y2={line.y2 + offY}
                  stroke="#6f4a23" strokeWidth={6} strokeLinecap="round" />
            <line x1={line.x1 - offX} y1={line.y1 - offY} x2={line.x2 - offX} y2={line.y2 - offY}
                  stroke="#6f4a23" strokeWidth={6} strokeLinecap="round" />
            {rungs}
          </g>
        );
      })}

      {/* L3 — snakes: tapering bodies, varied skins/patterns, real heads. */}
      {snakes.map((s, idx) => (
        <SnakeGraphic key={`snake-${idx}`} head={s.head} tail={s.tail} idx={idx} />
      ))}

      {/* L4 — tokens. Resting position animates via CSS transition; an
          active move is driven imperatively (WAAPI) by the page with the
          transition disabled for that one token (animatingKey). Clickable
          tokens render last (on top) with a big invisible hit pad. */}
      {orderedTokens.map((t) => {
        if (t.position === 0) return null; // off-board start, not rendered
        const c = centerOf(t.position);
        const clickable = !!t.clickable;
        const key = `${t.ownerColor}-${t.letter}`;
        const frozen = animatingKey === key;
        const place = t.position === 1 ? 'start' : t.position === 100 ? 'home' : 'normal';
        const slot = eggSlot(t.slotIndex ?? 0, t.slotCount ?? 1, place);
        // The active egg pops a touch above its slot so it's easy to tap in a
        // crowd — but never SMALLER than its slot (the lone board eggs are
        // already big at EGG_RUN_SCALE).
        const scale = clickable ? Math.min(1.75, Math.max(slot.scale * 1.1, 1.2)) : slot.scale;
        const dy = clickable ? slot.dy - 7 : slot.dy;
        const legs = clickable || frozen;   // legs show on your turn + while running
        return (
          <g
            key={key}
            data-tok={key}
            className={clickable ? 'sl-token sl-token--clickable' : 'sl-token'}
            style={{
              transition: frozen ? 'none' : `transform ${moveMs}ms ease-in-out`,
              transform:  `translate(${(c.x + slot.dx).toFixed(1)}px, ${(c.y + dy).toFixed(1)}px) scale(${scale})`,
              cursor: clickable ? 'pointer' : 'default',
            }}
            onClick={clickable && onPickToken ? () => onPickToken(t.letter) : undefined}
          >
            {clickable && (
              // "Your turn — tap this egg" ring. DARK so it stays visible on
              // the light pastel board, with a soft white glow so it also
              // pops against the dark snakes. (Was a low-contrast yellow.)
              <circle r={20} fill="none" stroke="#11331f" strokeWidth={3.5} opacity={0.95}
                      style={{ filter: 'drop-shadow(0 0 2.5px rgba(255,255,255,0.9))' }}>
                <animate attributeName="r" values="17;22;17" dur="1.1s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;1;0.6" dur="1.1s" repeatCount="indefinite" />
              </circle>
            )}
            <EggToken color={getColorHex(t.ownerColor)} legs={legs} />
            {clickable && (
              <circle r={26} fill="transparent" style={{ cursor: 'pointer' }} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

// A single egg token. Body is the seat colour with a soft shine + speckles
// (no letter — a player's two eggs are told apart by their board position).
// When `legs` is true (it's this egg's turn, or it's mid-run) two little
// chicken legs pop out the bottom and wiggle as if running.
function EggToken({ color, legs }: { color: string; legs: boolean }) {
  const egg = 'M0,-15 C8,-15 12,-5 12,3 C12,11 6.5,15.5 0,15.5 C-6.5,15.5 -12,11 -12,3 C-12,-5 -8,-15 0,-15 Z';
  const Leg = ({ hx, phase }: { hx: number; phase: string }) => (
    <g transform={`translate(${hx},10)`}>
      <g>
        <animateTransform attributeName="transform" type="rotate" values={phase} dur="0.42s" repeatCount="indefinite" />
        <line x1={0} y1={0} x2={0} y2={8} stroke="#e7892b" strokeWidth={2.2} strokeLinecap="round" />
        <line x1={0} y1={8} x2={-3.2} y2={11} stroke="#e7892b" strokeWidth={2.2} strokeLinecap="round" />
        <line x1={0} y1={8} x2={3.2} y2={11} stroke="#e7892b" strokeWidth={2.2} strokeLinecap="round" />
      </g>
    </g>
  );
  return (
    <g>
      {/* legs sit BEHIND the egg so feet peek out under it */}
      {legs && <><Leg hx={-4.5} phase="-20;20;-20" /><Leg hx={4.5} phase="20;-20;20" /></>}
      {/* ground shadow */}
      <ellipse cx={1} cy={16} rx={10} ry={3} fill="rgba(0,0,0,0.20)" />
      {/* egg body + speckles + shine */}
      <path d={egg} fill={color} stroke="rgba(0,0,0,0.14)" strokeWidth={1} />
      <ellipse cx={-4} cy={-5} rx={3.4} ry={5.2} fill="rgba(255,255,255,0.55)" />
      <circle cx={4} cy={1} r={1.3} fill="rgba(0,0,0,0.10)" />
      <circle cx={-2} cy={7} r={1.1} fill="rgba(0,0,0,0.10)" />
      <circle cx={6} cy={8} r={1} fill="rgba(0,0,0,0.10)" />
    </g>
  );
}

// The goal (square 100): a woven nest with a hen sitting on it — the eggs
// are racing home to her. Homed eggs render on top (L4) and huddle in the
// lower part of the cell so they read as eggs resting in the nest.
function NestAndHen({ cx, top }: { cx: number; top: number }) {
  const ny = top + 76;   // nest centre
  // Warm reddish-brown hen (Rhode-Island-Red look) so she stands out on the
  // pale pastel board. Cream chest + dark tail give depth and separate her
  // from the brown nest.
  const HEN_BODY = '#b5673a';
  const HEN_DARK = '#8a4a24';
  const HEN_TAIL = '#5e3417';
  const CHEST    = '#e8c79a';
  return (
    <g style={{ pointerEvents: 'none' }}>
      {/* soft shadow under the nest */}
      <ellipse cx={cx} cy={ny + 9} rx={40} ry={7} fill="rgba(0,0,0,0.18)" />
      {/* hen (behind/above the nest) */}
      <g>
        {/* layered tail feathers (dark → mid) */}
        <path d={`M ${cx - 16} ${top + 44} Q ${cx - 46} ${top + 18} ${cx - 34} ${top + 52} Z`} fill={HEN_TAIL} />
        <path d={`M ${cx - 16} ${top + 46} Q ${cx - 40} ${top + 24} ${cx - 30} ${top + 54} Z`} fill={HEN_DARK} />
        <path d={`M ${cx - 16} ${top + 48} Q ${cx - 33} ${top + 32} ${cx - 24} ${top + 56} Z`} fill={HEN_BODY} />
        {/* body */}
        <ellipse cx={cx} cy={top + 48} rx={24} ry={19} fill={HEN_BODY} />
        {/* cream chest/breast (front, lower) */}
        <path d={`M ${cx + 6} ${top + 38} Q ${cx + 24} ${top + 46} ${cx + 18} ${top + 63} Q ${cx + 6} ${top + 66} ${cx + 2} ${top + 56} Q ${cx + 2} ${top + 46} ${cx + 6} ${top + 38} Z`} fill={CHEST} opacity={0.92} />
        {/* folded wing */}
        <path d={`M ${cx - 8} ${top + 41} Q ${cx + 14} ${top + 37} ${cx + 12} ${top + 56} Q ${cx - 2} ${top + 53} ${cx - 8} ${top + 41} Z`} fill={HEN_DARK} />
        <path d={`M ${cx - 4} ${top + 45} Q ${cx + 9} ${top + 44} ${cx + 9} ${top + 53}`} stroke={HEN_TAIL} strokeWidth={1.4} fill="none" opacity={0.6} />
        {/* head */}
        <circle cx={cx + 14} cy={top + 31} r={10} fill={HEN_BODY} />
        {/* comb (3 red bumps) */}
        <circle cx={cx + 10} cy={top + 22} r={3} fill="#e23b27" />
        <circle cx={cx + 14.5} cy={top + 20} r={3.4} fill="#e23b27" />
        <circle cx={cx + 19} cy={top + 22} r={3} fill="#e23b27" />
        {/* beak (two-tone) */}
        <path d={`M ${cx + 23} ${top + 30} L ${cx + 32} ${top + 32.5} L ${cx + 23} ${top + 35} Z`} fill="#f0a330" />
        <path d={`M ${cx + 23} ${top + 32.5} L ${cx + 32} ${top + 32.5} L ${cx + 23} ${top + 35} Z`} fill="#d4811f" />
        {/* wattle */}
        <ellipse cx={cx + 22} cy={top + 37.5} rx={2.5} ry={4.2} fill="#e23b27" />
        {/* eye + highlight */}
        <circle cx={cx + 16} cy={top + 29.5} r={2} fill="#241a12" />
        <circle cx={cx + 16.7} cy={top + 28.8} r={0.7} fill="#fff" />
      </g>
      {/* nest (front) — woven basket: rim, bowl, and braided twigs */}
      <ellipse cx={cx} cy={ny} rx={43} ry={16} fill="#6e4423" />
      <ellipse cx={cx} cy={ny} rx={43} ry={16} fill="none" stroke="#83562c" strokeWidth={2} />
      <ellipse cx={cx} cy={ny - 1} rx={37} ry={12} fill="#a9743f" />
      <ellipse cx={cx} cy={ny - 2.5} rx={28} ry={7.5} fill="#583316" />
      {/* braided rim — alternating light/dark short twigs all around */}
      {[-38, -30, -22, -14, -6, 2, 10, 18, 26, 34].map((dx, i) => (
        <line key={i} x1={cx + dx} y1={ny - 7} x2={cx + dx + 8} y2={ny + 7}
              stroke={i % 2 ? '#c79a5f' : '#5e3a1d'} strokeWidth={2.4} strokeLinecap="round" opacity={0.85} />
      ))}
      {/* a few loose straws on the rim */}
      <path d={`M ${cx - 30} ${ny - 4} q 6 -5 13 -3`} stroke="#d8b277" strokeWidth={1.4} fill="none" opacity={0.8} />
      <path d={`M ${cx + 18} ${ny - 5} q 7 -4 14 0`} stroke="#d8b277" strokeWidth={1.4} fill="none" opacity={0.8} />
    </g>
  );
}

// ─── Snake graphic ────────────────────────────────────────────────
// Tapering body (fat middle → thin tail), a varied skin + pattern, and
// a real head (side-profile or top-down) with eyes, slit pupils and a
// forked tongue. Ported from the reference canvas drawSnakes into SVG.

type SnakeSkin = {
  body: string; outline: string; pattern: string;
  pat: 'triangles' | 'circles' | 'ovals' | 'stripes' | 'diamonds';
  head: 'side' | 'top';
};
// Cute cartoon snakes (the style the user loved): borderless face, flat
// body, light scale spots. Each snake gets its OWN colour, its own scale
// SHAPE and a side- or top-view head, so they all have distinct
// personalities. The face colour is just the body colour (no border).
// idx % length picks the skin, so the set repeats gracefully for any
// SNAKES_PER_BOARD.
const SNAKE_SKINS: SnakeSkin[] = [
  { body: '#7cb83f', outline: '#5d9430', pattern: '#bfe585', pat: 'triangles', head: 'side' }, // grass green — triangles
  { body: '#34b39c', outline: '#1f8f7c', pattern: '#8fe7d6', pat: 'circles',   head: 'top'  }, // teal — circles
  { body: '#c2a93c', outline: '#8f7d24', pattern: '#ecdd7e', pat: 'stripes',   head: 'side' }, // olive gold — stripes
  { body: '#5793cf', outline: '#3a6fa3', pattern: '#aed4ef', pat: 'ovals',     head: 'top'  }, // blue — ovals
  { body: '#d98a46', outline: '#a85f29', pattern: '#f4cb9a', pat: 'triangles', head: 'side' }, // coral/orange — triangles
  { body: '#a06cc8', outline: '#754a98', pattern: '#d8bcec', pat: 'circles',   head: 'top'  }, // violet — circles
];

// Cute side-profile head (copied from the reference art): a small rounded
// head that blends into the neck at the back, bulges over a smooth crown,
// rounds down a short snout to a soft nose, has a tiny mouth notch where
// the tongue exits, then a gentle lower jaw back to the neck. Local frame:
// +x = forward (snout/tongue), -y = up (eye on top). `h` = head scale.
function sideHeadPath(h: number): string {
  return [
    `M ${(-0.45 * h).toFixed(1)} ${(-0.42 * h).toFixed(1)}`,        // neck top (back)
    `Q ${(0.30 * h).toFixed(1)} ${(-1.02 * h).toFixed(1)} ${(1.02 * h).toFixed(1)} ${(-0.70 * h).toFixed(1)}`, // rounded crown
    `Q ${(1.55 * h).toFixed(1)} ${(-0.48 * h).toFixed(1)} ${(1.56 * h).toFixed(1)} ${(-0.04 * h).toFixed(1)}`, // snout down to nose tip
    `Q ${(1.52 * h).toFixed(1)} ${(0.20 * h).toFixed(1)} ${(1.16 * h).toFixed(1)} ${(0.14 * h).toFixed(1)}`,   // tiny mouth notch
    `Q ${(1.44 * h).toFixed(1)} ${(0.34 * h).toFixed(1)} ${(1.26 * h).toFixed(1)} ${(0.56 * h).toFixed(1)}`,   // lower lip bulge
    `Q ${(0.78 * h).toFixed(1)} ${(0.80 * h).toFixed(1)} ${(-0.45 * h).toFixed(1)} ${(0.60 * h).toFixed(1)}`,  // jaw back to neck bottom
    'Z',
  ].join(' ');
}

// Cute top-down head (a few snakes use this instead of the side profile):
// a symmetric leaf/almond shape pointing forward, two eyes (one each side),
// borderless. Local frame: +x = forward (snout/tongue), y symmetric.
function topHeadPath(h: number): string {
  return [
    `M ${(-0.45 * h).toFixed(1)} ${(-0.52 * h).toFixed(1)}`,
    `Q ${(0.42 * h).toFixed(1)} ${(-0.94 * h).toFixed(1)} ${(1.05 * h).toFixed(1)} ${(-0.42 * h).toFixed(1)}`, // left flank → snout
    `Q ${(1.5 * h).toFixed(1)} ${(-0.13 * h).toFixed(1)} ${(1.56 * h).toFixed(1)} 0`,                          // rounded snout tip
    `Q ${(1.5 * h).toFixed(1)} ${(0.13 * h).toFixed(1)} ${(1.05 * h).toFixed(1)} ${(0.42 * h).toFixed(1)}`,    // right flank
    `Q ${(0.42 * h).toFixed(1)} ${(0.94 * h).toFixed(1)} ${(-0.45 * h).toFixed(1)} ${(0.52 * h).toFixed(1)}`,  // back to neck
    'Z',
  ].join(' ');
}

function PatternMark({ kind, hw, color }: { kind: SnakeSkin['pat']; hw: number; color: string }) {
  if (kind === 'triangles') {
    // Small filled triangle scale. Caller varies the rotation so they
    // point different ways (hand-scattered look).
    const t = hw * 0.56;
    return <polygon points={`0,${-t} ${(t * 0.92).toFixed(1)},${(t * 0.72).toFixed(1)} ${(-t * 0.92).toFixed(1)},${(t * 0.72).toFixed(1)}`} fill={color} />;
  }
  if (kind === 'circles') {
    return <circle r={hw * 0.42} fill={color} />;
  }
  if (kind === 'ovals') {
    // Oriented along the body by the caller's rotation.
    return <ellipse rx={hw * 0.55} ry={hw * 0.3} fill={color} />;
  }
  if (kind === 'diamonds') {
    return <polygon points={`0,${-hw * 0.7} ${hw * 0.5},0 0,${hw * 0.7} ${-hw * 0.5},0`} fill={color} />;
  }
  // stripes — a band across the body (caller centres it on the spine and
  // rotates it to the body tangent so it reads as a cross-band).
  return <rect x={-hw * 0.22} y={-hw * 0.92} width={hw * 0.44} height={hw * 1.84} rx={hw * 0.2} fill={color} />;
}

function SnakeGraphic({ head, tail, idx }: { head: number; tail: number; idx: number }) {
  // Curve shape is derived from head+tail (same helper the bite-animation
  // uses) so the body the token slithers down ALWAYS matches what's drawn.
  const kind = snakeKind(head, tail);
  const skin = SNAKE_SKINS[idx % SNAKE_SKINS.length];
  const maxRadius = 13 + (idx % 3) * 2;            // 13/15/17 — size variety
  const g = useMemo(() => snakeGeometry(head, tail, kind, maxRadius), [head, tail, kind, maxRadius]);
  const N = g.points.length - 1;
  // Head scale — slightly bigger than the body width so the head reads
  // clearly (the user found the previous 1.05 too small). Kept modest.
  const h = maxRadius * 1.3;
  const headDeg = (g.headAngle * 180) / Math.PI;
  const flipSide = Math.cos(g.headAngle) < 0;       // keep eye up / jaw down

  // Scale markers along the body. Stripes are centred cross-bands; the
  // "spot" shapes (triangles/circles/ovals/diamonds) are nudged off the
  // spine and rotated for a hand-scattered look.
  const isStripe = skin.pat === 'stripes';
  const marks: JSX.Element[] = [];
  const iStart = Math.floor(N * 0.14);
  const iEnd = Math.floor(N * 0.9);
  const step = Math.max(2, Math.round(N / (isStripe ? 9 : 16)));
  let k = 0;
  for (let i = iStart; i <= iEnd; i += step, k++) {
    const p = g.points[i];
    const ang = g.angles[i];
    const hw = g.halfWidths[i];
    const off = isStripe ? 0 : ((k % 3) - 1) * hw * 0.42;   // scattered vs centred
    const nx = Math.cos(ang + Math.PI / 2) * off;
    const ny = Math.sin(ang + Math.PI / 2) * off;
    // stripes & ovals follow the body tangent; spots get varied spin.
    const rot = (ang * 180) / Math.PI + (isStripe || skin.pat === 'ovals' ? 0 : ((k * 71) % 360));
    marks.push(
      <g key={i} transform={`translate(${(p.x + nx).toFixed(1)} ${(p.y + ny).toFixed(1)}) rotate(${rot.toFixed(1)})`}>
        <PatternMark kind={skin.pat} hw={hw} color={skin.pattern} />
      </g>,
    );
  }

  return (
    <g>
      {/* soft same-hue darker rim + flat body fill (face stays borderless) */}
      <path d={g.outlinePath} fill={skin.outline} />
      <path d={g.bodyPath} fill={skin.body} />
      {marks}
      {/* head — borderless, body-coloured face. Side = single eye; top =
          two eyes. Both get a thin red forked tongue. */}
      <g transform={`translate(${g.headPt.x.toFixed(1)} ${g.headPt.y.toFixed(1)}) rotate(${headDeg.toFixed(1)})`}>
        {skin.head === 'top' ? (
          <g>
            <path d={topHeadPath(h)} fill={skin.body} />
            {/* two eyes, one each side of the snout */}
            <circle cx={h * 0.7} cy={-h * 0.42} r={h * 0.19} fill="#1c1c1c" />
            <circle cx={h * 0.7} cy={h * 0.42} r={h * 0.19} fill="#1c1c1c" />
            {/* forked tongue straight forward */}
            <path
              d={`M ${(h * 1.5).toFixed(1)} 0 L ${(h * 2.05).toFixed(1)} 0 `
                + `M ${(h * 2.05).toFixed(1)} 0 L ${(h * 2.35).toFixed(1)} ${(-h * 0.22).toFixed(1)} `
                + `M ${(h * 2.05).toFixed(1)} 0 L ${(h * 2.35).toFixed(1)} ${(h * 0.22).toFixed(1)}`}
              stroke="#ef5b45" strokeWidth={Math.max(1.3, h * 0.1)} fill="none"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </g>
        ) : (
          <g transform={flipSide ? 'scale(1,-1)' : undefined}>
            <path d={sideHeadPath(h)} fill={skin.body} />
            {/* single round black eye, upper-front of the head */}
            <circle cx={h * 0.62} cy={-h * 0.34} r={h * 0.20} fill="#1c1c1c" />
            {/* thin red forked tongue out of the mouth notch */}
            <path
              d={`M ${(h * 1.5).toFixed(1)} ${(h * 0.04).toFixed(1)} `
                + `L ${(h * 2.05).toFixed(1)} ${(h * 0.04).toFixed(1)} `
                + `M ${(h * 2.05).toFixed(1)} ${(h * 0.04).toFixed(1)} L ${(h * 2.35).toFixed(1)} ${(-h * 0.18).toFixed(1)} `
                + `M ${(h * 2.05).toFixed(1)} ${(h * 0.04).toFixed(1)} L ${(h * 2.35).toFixed(1)} ${(h * 0.26).toFixed(1)}`}
              stroke="#ef5b45" strokeWidth={Math.max(1.3, h * 0.11)} fill="none"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </g>
        )}
      </g>
    </g>
  );
}
